/**
 * A lógica da tela de planos, sem JSX — como todo o resto desta camada.
 *
 * Aqui moram as decisões que erram caro num paywall: qual plano recebe botão de
 * assinar (mandar assinar quem já assinou é o jeito mais rápido de perder um
 * cliente pagante), o que a tela promete, e como o sinal mais crítico é
 * apresentado.
 *
 * A regra de vocabulário do produto vale inteira aqui: **Sinal** responde O QUE
 * está acontecendo e é gratuito; **Parecer** responde POR QUE e o que atacar
 * primeiro, e é pago. O teaser é um SINAL — ele é determinístico, calculado
 * pelas mesmas 39 regras que o usuário já vê de graça. Apresentá-lo como se
 * fosse prosa de IA seria vender uma amostra falsa do que se está comprando.
 */

import { TELAS, telaDoIndicador } from '../diagnostico/catalogo';
import { rotuloLongoDaCompetencia } from '../services/loteDoMes';

const MOEDA_INTEIRA = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 0
});

/**
 * Preço com periodicidade colada, porque preço sem periodicidade é preço pela
 * metade: R$ 149 uma vez e R$ 149 por mês são produtos diferentes.
 */
export const rotuloDePreco = (plano) => {
  // SOB ORÇAMENTO VEM ANTES DE TUDO, E POR UM MOTIVO CARO: `preco: null`
  // significa "de graça" num plano e "a combinar" no outro. Os dois estados
  // têm a mesma cara em JavaScript e significados opostos numa tabela de
  // preço. Sem esta primeira linha, o Full Time renderiza "Grátis".
  if (plano?.sobOrcamento) {
    return { valor: 'Sob orçamento', periodicidade: null, sobOrcamento: true };
  }
  if (!plano || plano.preco === 0 || plano.preco === null || plano.preco === undefined) {
    return { valor: 'Grátis', periodicidade: null, sobOrcamento: false };
  }
  return {
    valor: MOEDA_INTEIRA.format(plano.preco),
    periodicidade: plano.periodicidade || null,
    sobOrcamento: false
  };
};

/**
 * As colunas do comparativo, já resolvidas.
 *
 * Nenhum plano vem pré-selecionado e nenhum vem marcado como "recomendado": num
 * produto financeiro, empurrar o plano caro é o mesmo comportamento que a tela
 * inteira existe para combater.
 */
/**
 * O que o botão de cada plano diz.
 *
 * Três eixos, e os três mudam a frase: se a pessoa já pediu, se ela tem conta,
 * e se o plano se assina ou se orça. "Assinar" um plano que é vendido sob
 * orçamento promete um checkout que não existe — e a primeira coisa que o
 * cliente faz depois de clicar é descobrir isso.
 */
const rotuloDoBotao = ({ plano, jaPediu, autenticado }) => {
  const sobOrcamento = Boolean(plano.sobOrcamento);
  if (jaPediu) return sobOrcamento ? 'Orçamento solicitado' : 'Pedido enviado';
  if (!autenticado) return sobOrcamento ? 'Entrar para solicitar' : 'Entrar para assinar';
  return sobOrcamento ? 'Solicitar orçamento' : `Quero o ${plano.nome}`;
};

export const linhasDoComparativo = (catalogo, planoAtual, { autenticado = false, intencao = null } = {}) => {
  const lista = Array.isArray(catalogo) ? catalogo : [];
  const atual = planoAtual || 'gratuito';

  return lista.map((plano) => {
    const eAtual = plano.id === atual;
    const jaPediu = intencao?.plano === plano.id;
    const preco = rotuloDePreco(plano);

    return {
      id: plano.id,
      nome: plano.nome,
      para: plano.para || null,
      preco: preco.valor,
      periodicidade: preco.periodicidade,
      entrega: plano.entrega || [],
      nota: plano.nota || null,
      limiteCompetencias: plano.limiteCompetencias ?? null,
      // O cartão precisa saber disto para não desenhar "Sob orçamento" na mesma
      // escala de 2,5rem que existe para um número.
      sobOrcamento: Boolean(plano.sobOrcamento),
      atual: eAtual,
      jaPediu,
      // Sem CTA no plano que a pessoa já tem, e sem CTA no gratuito — ele não
      // se contrata, ele é o ponto de partida.
      cta: eAtual || plano.id === 'gratuito'
        ? null
        : {
          // O RÓTULO INTEIRO SAI DAQUI, e não metade aqui e metade no
          // componente: eram dois lugares decidindo a mesma frase, e o dia em
          // que apareceu um plano que não se "assina" o componente continuou
          // dizendo "Entrar para assinar" para quem ia pedir um orçamento.
          // Aqui a decisão é uma só, e o teste alcança ela.
          rotulo: rotuloDoBotao({ plano, jaPediu, autenticado }),
          // Sem conta não existe empresa para associar o pedido. Dizer isso no
          // rótulo evita o clique que devolve 401.
          acao: autenticado ? 'pedir' : 'entrar',
          desabilitado: jaPediu
        }
    };
  });
};

/**
 * O sinal mais crítico, no formato Fato → Ação, com o contador do resto.
 *
 * Três estados, todos previstos: sem dado (a pessoa chegou na tela antes de
 * lançar qualquer coisa), sem sinal (a empresa está bem — acontece, e a tela
 * não pode quebrar por isso) e com sinal.
 */
export const estadoDoTeaser = (teaser, { temDados = true } = {}) => {
  if (!temDados) {
    return { estado: 'semDados', fato: null, acao: null, restantes: 0, tela: null };
  }

  const insight = teaser?.insight || null;
  const total = Number.isFinite(teaser?.totalDeInsights) ? teaser.totalDeInsights : 0;

  if (!insight) {
    return { estado: 'semSinal', fato: null, acao: null, restantes: Math.max(0, total), tela: null };
  }

  return {
    estado: 'comSinal',
    severidade: insight.severity || 'info',
    // `title` não entra: as 39 regras não preenchem esse campo (o serviço o
    // emite sempre vazio), e um cabeçalho em branco acima do fato é ruído.
    fato: insight.message || null,
    acao: insight.recommendation || null,
    // O que sobra depois de mostrar um. É o número que dá tamanho ao que o
    // Parecer tem para cruzar — e ele é honesto porque é contagem, não promessa.
    restantes: Math.max(0, total - 1),
    tela: telaDoIndicador(insight.evidence?.[0]?.indicator) || TELAS.ANALISE
  };
};

/**
 * A oferta que aparece quando um lançamento acaba de ser concluído.
 *
 * QUANDO, E POR QUÊ AQUI: é o único instante em que o produto já entregou o
 * valor. A pessoa acabou de fechar um mês (ou terminar o questionário), a série
 * cresceu e os números novos estão na tela. Oferecer assinatura antes disso é
 * anúncio; oferecer depois, com o resultado à vista, é a continuação de uma
 * frase que o produto começou.
 *
 * O QUE ELA NÃO PODE FAZER: virar banner permanente, e atrapalhar quem acabou
 * de lançar e quer ver o resultado. Por isso ela é dispensável, nasce só neste
 * gesto e some quando a pessoa troca de tela.
 *
 * HONESTIDADE: a lista do que a pessoa JÁ TEM vem primeiro, inteira, e sai do
 * mesmo catálogo que a tela de planos usa — escrever uma segunda lista à mão
 * seria criar duas versões da promessa, que divergem no dia em que alguém mexer
 * numa delas. E nada aqui aciona modelo: é montagem de texto sobre um catálogo.
 */
export const ofertaAoConcluirLancamento = ({
  planoAtual,
  catalogo,
  competencia = null,
  corrigiu = false
} = {}) => {
  // Quem já assina não recebe oferta. Oferecer a alguém o que ele já comprou
  // faz o produto parecer que não sabe quem está na frente dele.
  if ((planoAtual || 'gratuito') !== 'gratuito') return null;

  // Correção NÃO é lançamento. A pessoa voltou para arrumar um número que já
  // era dela; interromper isso com uma oferta é cobrar pedágio de quem está
  // caprichando no próprio dado — e corrigir é justamente o que o produto
  // promete continuar liberado em qualquer plano.
  if (corrigiu) return null;

  const lista = Array.isArray(catalogo) ? catalogo : [];
  const gratuito = lista.find((p) => p.id === 'gratuito') || null;
  // O PLANO SOB ORÇAMENTO NÃO ENTRA AQUI. Esta oferta nasce de um gesto — a
  // pessoa acabou de fechar um mês — e o que cabe nesse instante é um preço
  // que ela pode decidir sozinha. Emendar "fale com a gente para saber quanto
  // custa" no fim de um lançamento é trocar um resultado por uma reunião.
  // Sem a segunda condição isto funcionaria por acidente de ordem do catálogo.
  const pago = lista.find((p) => p.id !== 'gratuito' && !p.sobOrcamento) || null;
  // Sem catálogo não há oferta: inventar preço ou lista de entrega no frontend
  // é a forma mais rápida de o produto prometer o que não vende.
  if (!pago) return null;

  const preco = rotuloDePreco(pago);

  return {
    titulo: competencia
      ? `${rotuloLongoDaCompetencia(competencia)} entrou na sua série`
      : 'Seu lançamento entrou na série',
    // Dito antes de qualquer preço: o que está na tela continua inteiro e de
    // graça. A assinatura não destrava o que a pessoa acabou de ver.
    frase: 'Tudo o que você está vendo agora continua seu, sem assinatura.',
    jaTem: gratuito?.entrega || [],
    notaDoGratuito: gratuito?.nota || null,
    // "Tudo do Gratuito" é verdadeiro no comparativo da tela de planos, onde as
    // duas colunas aparecem lado a lado. Aqui ele seria ruído: a lista de cima
    // acabou de dizer, item por item, o que é o gratuito.
    ganha: (pago.entrega || []).filter(
      (item) => String(item).trim().toLowerCase() !== 'tudo do gratuito'
    ),
    plano: {
      id: pago.id,
      nome: pago.nome,
      preco: preco.valor,
      periodicidade: preco.periodicidade
    }
  };
};

/**
 * A frase de contexto do topo — por que esta tela apareceu.
 *
 * Ela muda conforme o caminho que trouxe a pessoa até aqui, porque "assine" sem
 * motivo é anúncio, e com motivo é resposta a uma pergunta que ela acabou de
 * fazer.
 */
export const motivoDaVisita = ({ origem, mesesBloqueados = [], planoAtual = 'gratuito' } = {}) => {
  if (origem === 'mes-bloqueado' && mesesBloqueados.length) {
    return {
      titulo: mesesBloqueados.length === 1
        ? 'Um mês a mais precisa de assinatura'
        : `${mesesBloqueados.length} meses a mais precisam de assinatura`,
      // Corrigir o próprio dado nunca foi cobrado, e é a primeira coisa a dizer:
      // sem ela, o bloqueio lê como "meus dados foram congelados".
      frase: 'Corrigir ou completar os meses que você já lançou continua liberado, em qualquer plano.'
    };
  }

  if (origem === 'lancamento-concluido') {
    return {
      titulo: 'O que a assinatura acrescenta ao que você acabou de lançar',
      frase: 'O mês que você fechou, os sinais, o índice de saúde e o plano de ação são seus e '
        + 'continuam gratuitos. Aqui está só o que muda se você assinar.'
    };
  }

  if (origem === 'conexao-direta') {
    return {
      titulo: 'A conexão direta é o plano Full Time',
      // O PREÇO NÃO ESTÁ AQUI PORQUE NÃO EXISTE UM. Dizer isso antes de a
      // pessoa procurar evita que ela leia a coluna da direita achando que
      // esqueceram de preencher o valor.
      frase: 'Ela é implantada no sistema que a sua empresa já usa, sob projeto — por '
        + 'isso o valor sai de uma conversa, e não de uma tabela. Os outros dois planos '
        + 'continuam funcionando com planilha ou questionário, sem depender disso.'
    };
  }

  if (origem === 'parecer') {
    return {
      titulo: 'O Parecer do Consultor é do plano pago',
      frase: 'Os sinais, o índice de saúde, o plano de ação e o acompanhamento das iniciativas '
        + 'continuam seus, sem assinatura e sem enviar seus números para fora.'
    };
  }

  if (planoAtual !== 'gratuito') {
    return { titulo: 'Seu plano', frase: null };
  }

  return {
    // O TÍTULO DESTE BLOCO NÃO É "PLANOS". A tela já se chama Planos na barra do
    // topo e no <h1> logo acima; um terceiro "Planos" na mesma coluna faz a tela
    // parecer gerada. Este bloco fala do que a assinatura acrescenta — e é isso
    // que ele passa a dizer.
    titulo: 'O que a assinatura acrescenta',
    // A ASSINATURA NÃO É UMA ANÁLISE AVULSA — E O TÍTULO DA TELA NÃO PODE
    // SUGERIR QUE SEJA. A frase anterior ("o que a assinatura acrescenta é a
    // leitura dela") descrevia um parecer que se compra uma vez. O que se
    // assina é a continuidade: cada mês fechado entra na série, e é a série que
    // faz a leitura ficar mais precisa.
    frase: 'A análise inteira já é sua, todo mês, e continua sendo. O que a assinatura '
      + 'acrescenta é a série sem limite de meses — e a leitura que só existe quando há '
      + 'história suficiente atrás dos números.'
  };
};

/**
 * A frase sobre os meses que não entraram na série.
 *
 * Nomeia os meses porque "3 meses não foram salvos" manda a pessoa procurar
 * quais, e procurar dado que sumiu é a pior tarefa que um produto financeiro
 * pode dar ao usuário.
 */
export const frasesDoBloqueio = (bloqueio, rotularCompetencia) => {
  const meses = bloqueio?.bloqueados || [];
  if (!meses.length) return null;

  const rotulo = typeof rotularCompetencia === 'function' ? rotularCompetencia : (c) => c;
  const nomes = meses.map(rotulo);
  const lista = nomes.length === 1
    ? nomes[0]
    : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;

  return {
    titulo: meses.length === 1
      ? `${lista} não entrou no seu histórico`
      : `${lista} não entraram no seu histórico`,
    // O que a pessoa VÊ na tela agora continua certo: o cálculo é feito sobre o
    // que está no navegador. O que não sobrevive é o próximo acesso.
    corpo: bloqueio?.mensagem
      || 'O plano atual não comporta esses meses. Eles não foram gravados.',
    aindaPermitido: bloqueio?.aindaPermitido
      || 'Corrigir ou completar os meses que você já lançou continua liberado.',
    meses: nomes
  };
};
